import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { forkJoin, map, Observable, of, tap } from 'rxjs';
import { ApiResponsePaged } from '@core/interfaces/api.interface';
import { BaseService } from '@core/services/base.service';
import { mapPagedResponse } from '@core/utils/mapper-response-paged.util';
import {
  ALERT_CHECKLIST_WHERE_WITH_ASSIGNED_WORK,
  ALERT_CHECKLIST_WHERE_WITHOUT_ASSIGNED_WORK,
  INCIDENT_WHERE_WITH_ASSIGNED_WORK,
  INCIDENT_WHERE_WITHOUT_ASSIGNED_WORK,
} from '@features/advanced-maintenance/constants/work-order-detail-filters.constants';
import {
  EventualityAddPayload,
  EventualityAddWithMaintenanceWorkPayload,
  MaintenanceAddPayload,
  MaintenanceWorkAddPayload,
  WorkOrder,
  WorkOrderCancelPayload,
  WorkOrderClosePayload,
  WorkOrderDetailOTWorkForm,
  WorkOrderFilter,
  WorkOrderForm,
  WorkOrderReopenPayload,
  WorkOrderTabCounts,
} from '@features/advanced-maintenance/interfaces/work-orders.interface';
import { MaintenanceSchedulesService } from '@features/advanced-maintenance/services/maintenance-schedules.service';
import {
  AlertsQueryParams,
  VehicleAlertsService,
} from '@features/reports/views/reports-checklists/report-alert-checklist/services/vehicleAlert.service';
import { VehicleEventualityFilters } from '@features/vehicles/interfaces/vehicle.interface';
import { VehiclesService } from '@features/vehicles/services/vehicles.service';

const EMPTY_TAB_COUNTS: WorkOrderTabCounts = {
  incidents: 0,
  checklistAlerts: 0,
  maintenanceAlerts: 0,
};

@Injectable({ providedIn: 'root' })
export class WorkOrdersService extends BaseService {
  currentWorkOrder = signal<WorkOrder | null>(null);

  /** Totales de incidentes y alertas del detalle de OT activo. */
  tabCounts = signal<WorkOrderTabCounts | null>(null);

  private readonly vehiclesService = inject(VehiclesService);
  private readonly vehicleAlertsService = inject(VehicleAlertsService);
  private readonly maintenanceSchedulesService = inject(MaintenanceSchedulesService);

  constructor() {
    super('workOrders');
  }

  /**
   * Obtiene todas las ordenes de trabajo.
   * @param params - Los parámetros para filtrar las ordenes de trabajo.
   * @returns Observable<ApiResponsePaged<WorkOrder>>
   */
  getAll(params: WorkOrderFilter): Observable<ApiResponsePaged<WorkOrder>> {
    return this.GET<HttpResponse<WorkOrder[]>>('get-all', params, {
      observe: 'response',
    }).pipe(mapPagedResponse<WorkOrder>());
  }

  /**
   * Obtiene una orden de trabajo por su ID.
   * @param id - El ID de la orden de trabajo.
   * @returns Observable<WorkOrder>
   */
  getById(id: string): Observable<WorkOrder> {
    return this.GET<WorkOrder>(`get-by-id/${id}`).pipe(
      map((workOrder) => {
        this.currentWorkOrder.set(workOrder);
        return workOrder;
      }),
    );
  }

  /**
   * Crea una orden de trabajo.
   * @param payload - El payload para crear la orden de trabajo.
   * @returns Observable<WorkOrder>
   */
  create(payload: Omit<WorkOrderForm, 'id'>): Observable<WorkOrder> {
    return this.POST<WorkOrder>('create', payload);
  }

  /**
   * Actualiza una orden de trabajo.
   * @param id - El ID de la orden de trabajo.
   * @param payload - El payload para actualizar la orden de trabajo.
   * @returns Observable<WorkOrder>
   */
  update(id: string, payload: WorkOrderForm): Observable<WorkOrder> {
    return this.PUT<WorkOrder>(`update/${id}`, payload);
  }

  /**
   * Cierra una orden de trabajo.
   * @param id - El ID de la orden de trabajo.
   * @param payload - El payload para cerrar la orden de trabajo.
   * @returns Observable<void>
   */
  close(id: string, payload: WorkOrderClosePayload): Observable<void> {
    return this.PUT<void>(`closed/${id}`, payload);
  }

  /**
   * Anula una orden de trabajo.
   * @param id - El ID de la orden de trabajo.
   * @param payload - El payload para anular la orden de trabajo.
   * @returns Observable<void>
   */
  cancel(id: string, payload: WorkOrderCancelPayload): Observable<void> {
    return this.PUT<void>(`cancel/${id}`, payload);
  }

  /**
   * Reabre una orden de trabajo.
   * @param id - El ID de la orden de trabajo.
   * @param payload - El payload para reabrir la orden de trabajo.
   * @returns Observable<void>
   */
  reopen(id: string, payload: WorkOrderReopenPayload): Observable<void> {
    return this.PUT<void>(`re-open/${id}`, payload);
  }

  /**
   * @description
   * Asocia una eventualidad a una OT.
   * @param {string} workOrderId Id de la orden de trabajo.
   * @param {EventualityAddPayload} payload Datos de asociación.
   * @returns {Observable<void>}
   */
  eventualityAdd(workOrderId: string, payload: EventualityAddPayload): Observable<void> {
    return this.PUT<void>(`eventuality-add/${workOrderId}`, payload);
  }

  /**
   * @description
   * Asocia una eventualidad a un trabajo de mantenimiento dentro de una OT.
   * @param {string} workOrderId Id de la orden de trabajo.
   * @param {EventualityAddWithMaintenanceWorkPayload} payload Datos de asociación.
   * @returns {Observable<void>}
   */
  eventualityAddWithMaintenanceWork(
    workOrderId: string,
    payload: EventualityAddWithMaintenanceWorkPayload,
  ): Observable<void> {
    return this.PUT<void>(`eventuality-add-with-maintenance-work/${workOrderId}`, payload);
  }

  /**
   * @description
   * Asocia una alerta de mantenimiento a una OT.
   * @param {string} workOrderId Id de la orden de trabajo.
   * @param {MaintenanceAddPayload} payload Datos de asociación.
   * @returns {Observable<void>}
   */
  maintenanceAdd(workOrderId: string, payload: MaintenanceAddPayload): Observable<void> {
    return this.PUT<void>(`maintenance-add/${workOrderId}`, payload);
  }

  /**
   * @description
   * Asocia una alerta de mantenimiento a un trabajo dentro de una OT.
   * @param {string} workOrderId Id de la orden de trabajo.
   * @param {WorkOrderDetailOTWorkForm} payload Datos de asociación.
   * @returns {Observable<void>}
   */
  maintenanceWorkAdd(workOrderId: string, payload: WorkOrderDetailOTWorkForm): Observable<void> {
    return this.POST<void>(`maintenance-work-add/${workOrderId}`, payload);
  }

  /**
   * Exporta las ordenes de trabajo a Excel.
   * @param params - Los parámetros para exportar las ordenes de trabajo.
   * @returns Observable<HttpResponse<Blob>>
   */
  export(params: WorkOrderFilter): Observable<HttpResponse<Blob>> {
    return this.GET<HttpResponse<Blob>>('get-excel', params, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * Carga masiva de órdenes de trabajo.
   * El backend valida el archivo y responde con éxito o un Excel de errores.
   * @param businessId - ID de la empresa.
   * @param file - Archivo Excel diligenciado.
   * @returns Observable<HttpResponse<Blob>>
   */
  bulkUpload(businessId: string, file: File): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.POST<HttpResponse<Blob>>(`upload-excel/${businessId}`, formData, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * @description
   * Obtiene los totales de incidentes y alertas del vehículo de la orden,
   * independientemente del filtro con/sin trabajo asignado.
   *
   * @param {WorkOrder} workOrder Orden de trabajo activa.
   * @returns {Observable<WorkOrderTabCounts>} Contadores por tab.
   */
  fetchTabCounts(workOrder: WorkOrder): Observable<WorkOrderTabCounts> {
    const { businessId, vehicleId } = workOrder;

    if (!businessId || !vehicleId) {
      return of(EMPTY_TAB_COUNTS);
    }

    const incidentBase = {
      businessId,
      vehicleId,
      isDeleted: false,
      pageNumber: 0,
      pageSize: 1,
    };

    const checklistBase: AlertsQueryParams = {
      BusinessesId: businessId,
      vehicleId,
      pageNumber: 1,
      pageSize: 1,
      isActive: true,
      isDeleted: false,
      orderBy: ['date,1', 'priority,1'],
    };

    return forkJoin({
      incidentsWith: this.getIncidentCount({
        ...incidentBase,
        whereAnd: INCIDENT_WHERE_WITH_ASSIGNED_WORK,
      }),
      incidentsWithout: this.getIncidentCount({
        ...incidentBase,
        whereAnd: INCIDENT_WHERE_WITHOUT_ASSIGNED_WORK,
      }),
      checklistWith: this.getChecklistAlertCount({
        ...checklistBase,
        whereAnd: ALERT_CHECKLIST_WHERE_WITH_ASSIGNED_WORK,
      }),
      checklistWithout: this.getChecklistAlertCount({
        ...checklistBase,
        whereAnd: ALERT_CHECKLIST_WHERE_WITHOUT_ASSIGNED_WORK,
      }),
      maintenanceAlerts: this.getMaintenanceAlertCount({
        businessId,
        vehicleId,
        pageNumber: 1,
        pageSize: 1,
        orderBy: ['dateCreated,1'],
      }),
    }).pipe(
      map(({ incidentsWith, incidentsWithout, checklistWith, checklistWithout, maintenanceAlerts }) => ({
        incidents: incidentsWith + incidentsWithout,
        checklistAlerts: checklistWith + checklistWithout,
        maintenanceAlerts,
      })),
    );
  }

  /**
   * @description
   * Recalcula y actualiza los totales de incidentes y alertas del detalle de OT activo.
   * Debe invocarse tras cualquier mutación que pueda cambiar cuántos incidentes o
   * alertas del vehículo siguen sin asociar a una orden de trabajo (crear/asociar un
   * trabajo a la OT, asociar un incidente o una alerta a la OT), ya que esos totales
   * no se recalculan solos: el badge de cada tab queda desactualizado hasta que se
   * vuelva a navegar a la ruta.
   * @param {WorkOrder} workOrder Orden de trabajo activa.
   * @returns {Observable<WorkOrderTabCounts>} Totales actualizados.
   */
  refreshTabCounts(workOrder: WorkOrder): Observable<WorkOrderTabCounts> {
    return this.fetchTabCounts(workOrder).pipe(
      tap((tabCounts) => this.tabCounts.set(tabCounts)),
    );
  }

  /**
   * @description
   * Limpia los totales de tabs del detalle de OT.
   * @returns {void}
   */
  clearTabCounts(): void {
    this.tabCounts.set(null);
  }

  /**
   * @description
   * Limpia la orden de trabajo activa para que vistas compartidas (p. ej.
   * alertas de programación) no sigan viendo el estado de una OT ya visitada
   * al salir del contexto de detalle/reporte/seguimiento de la orden.
   * @returns {void}
   */
  clearCurrentWorkOrder(): void {
    this.currentWorkOrder.set(null);
  }

  private getIncidentCount(
    params: VehicleEventualityFilters & { whereAnd?: string },
  ): Observable<number> {
    return this.vehiclesService
      .getVehicleEventualities(params)
      .pipe(map((response) => response.pagination?.totalElements ?? 0));
  }

  private getChecklistAlertCount(params: AlertsQueryParams): Observable<number> {
    return this.vehicleAlertsService
      .getAllLastDatePaged(params)
      .pipe(map((response) => response.pagination?.totalElements ?? 0));
  }

  private getMaintenanceAlertCount(
    params: Parameters<MaintenanceSchedulesService['getAll']>[0],
  ): Observable<number> {
    return this.maintenanceSchedulesService
      .getAll(params)
      .pipe(map((response) => response.pagination?.totalElements ?? 0));
  }

  formatWorkOrderLabel(order: WorkOrder): string {
    const date = new Date(order.orderDate);
    const formattedDate = Number.isNaN(date.getTime())
      ? order.orderDate
      : new Intl.DateTimeFormat('es-CO', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(date);

    return `Nro. Orden: ${order.orderNumber} - Fecha: ${formattedDate}`;
  }
}
